import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Award, XCircle, Clock, TrendingUp, DollarSign } from 'lucide-react';
import {
  formatPoints,
  getPointsTransactionColor,
  validatePointsAdjustment
} from '../../utils/pointsHelpers';

const PointsManagementModal = ({
  show,
  onClose,
  user,
  pointsHistory,
  pointsForm,
  onFormChange,
  onSubmit,
  loading,
  adjusting
}) => {
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validatePointsAdjustment(pointsForm.points, pointsForm.reason);
    if (!validation.isValid) {
      return;
    }

    // Call parent's submit handler
    if (onSubmit) {
      onSubmit(e);
    }
  };

  // Calculate points statistics
  const calculateStats = () => {
    if (!pointsHistory || pointsHistory.length === 0) {
      return {
        totalEarned: user?.totalPointsEarned || 0,
        totalSpent: user?.totalPointsRedeemed || 0,
        currentBalance: user?.pointsBalance || 0,
        totalTransactions: 0
      };
    }

    return {
      totalEarned: user?.totalPointsEarned || 0,
      totalSpent: user?.totalPointsRedeemed || 0,
      currentBalance: user?.pointsBalance || 0,
      totalTransactions: pointsHistory.length
    };
  };

  const stats = calculateStats();

  if (!show || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Points Management</h2>
                <p className="text-gray-600">{user.firstName} {user.lastName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {/* Points Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Current Balance</p>
              <p className="text-2xl font-bold text-purple-800">{formatPoints(stats.currentBalance)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Total Earned</p>
              <p className="text-2xl font-bold text-green-800">{formatPoints(stats.totalEarned)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm text-red-600 font-medium">Total Spent</p>
              <p className="text-2xl font-bold text-red-800">{formatPoints(stats.totalSpent)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Transactions</p>
              <p className="text-2xl font-bold text-blue-800">{stats.totalTransactions}</p>
            </div>
          </div>

          {/* Points Adjustment Form */}
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
              Adjust Points
            </h3>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Points Amount *
                </label>
                <input
                  type="number"
                  value={pointsForm.points}
                  onChange={(e) => onFormChange?.({ points: e.target.value })}
                  placeholder="Enter points (+ to add, - to subtract)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use positive numbers to add points, negative to subtract
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type *
                </label>
                <select
                  value={pointsForm.type}
                  onChange={(e) => onFormChange?.({ type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="ADMIN_ADJUSTMENT">Admin Adjustment</option>
                  <option value="BONUS_POINTS">Bonus Points</option>
                  <option value="COMPENSATION">Compensation</option>
                  <option value="CORRECTION">Correction</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason *
              </label>
              <textarea
                value={pointsForm.reason}
                onChange={(e) => onFormChange?.({ reason: e.target.value })}
                placeholder="Explain why you're adjusting points..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <button
              type="submit"
              disabled={adjusting}
              className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {adjusting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adjusting Points...
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5 mr-2" />
                  Adjust Points
                </>
              )}
            </button>
          </form>

          {/* Points Transaction History */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-gray-600" />
              Transaction History
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : pointsHistory.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Transaction History</h3>
                <p className="text-gray-600">This user hasn't had any points transactions yet.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pointsHistory.map((transaction) => {
                  const isPositive = transaction.points > 0;
                  const transactionColor = getPointsTransactionColor(transaction.type);

                  return (
                    <div
                      key={transaction._id}
                      className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      {/* Transaction Info */}
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            transaction.type === 'EARNED_REFERRAL' ? 'bg-green-500' :
                            transaction.type === 'ADMIN_ADJUSTMENT' ? 'bg-purple-500' :
                            transaction.type === 'REDEEMED_DISCOUNT' ? 'bg-orange-500' :
                            transaction.type === 'BONUS_POINTS' ? 'bg-blue-500' :
                            transaction.type === 'COMPENSATION' ? 'bg-yellow-500' :
                            'bg-gray-500'
                          }`}></div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{transaction.description}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(transaction.createdAt).toLocaleDateString()} • {transaction.type.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Points Change */}
                      <div className="text-right ml-4">
                        <p className={`text-lg font-bold ${
                          isPositive ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isPositive ? '+' : ''}{formatPoints(transaction.points)} pts
                        </p>
                        {transaction.newBalance !== undefined && (
                          <p className="text-sm text-gray-500">
                            Balance: {formatPoints(transaction.newBalance)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsManagementModal;
